import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTransactionDetail } from "@/hooks/useTransactionDetail";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  ExternalLink,
  Image,
  FileText,
  ArrowLeft,
  Copy,
  ArrowUpRightIcon,
} from "lucide-react";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";
import { cn, copyToClipboard, cropId } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ShareButton } from "@/components/ShareButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";
import formatCO2eCredits from "@/utils/formatAmountToCO2Credits";
import NotFound from "@/components/NotFound";
import { usePurchaseDetail } from "@/hooks/usePurchaseDetail";
import { Purchase, Transaction } from "@/types";
import getProjectTranslation from "@/utils/getProjectTranslation";
import UnsafeMarkdown from "@/components/UnsafeMarkdown";
import formatMultiple from "@/utils/formatMultiple";
import RetirementsList from "@/components/RetirementList";
import TransactionAccountInformation from "@/components/TransactionAccountInformation";

const CONFIRMATION_CARD_ID = "transaction-confirmation-card";
const BATCH_HIDDEN_CUSTOMER_NAMES = ["SAP"];
const NAME_HIDDEN_CUSTOMER_NAMES = ["Earthly"];

const DATA_FETCHER = {
  transaction: useTransactionDetail,
  purchase: usePurchaseDetail,
};

function removeDuplicates<T>(array: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    } else {
      seen.add(key);
      return true;
    }
  });
}

function transactionToPurchase(transaction: Transaction): Purchase {
  return {
    id: transaction.id,
    ts: transaction.ts,
    customerName: transaction.nameOnConfirmation || transaction.customerName,
    organization: transaction.organization,
    description: transaction.description,
    transactions: [transaction],
  };
}

const TransactionConfirmation = () => {
  const [searchParams] = useSearchParams();
  const accessId = searchParams.get("accessId");
  const type = searchParams.get("type") || "transaction";
  const { t, language } = useLanguage();

  const notFound = (
    <NotFound
      title={t(`confirmation.${type}.notFound.title`)}
      description={t(`confirmation.${type}.notFound.description`)}
      footer={
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t(`confirmation.${type}.notFound.back`)}
        </Button>
      }
    />
  );

  useEffect(() => {
    // Set the page title to "Silvaconsult Registry | Confirmation"
    document.title = "Silvaconsult Registry | Confirmation";
    window.scrollTo(0, 0);
  }, []);

  const fetchFunc = DATA_FETCHER[type as keyof typeof DATA_FETCHER];

  if (!fetchFunc) return notFound;

  const { data, isLoading, isError } = fetchFunc(accessId || undefined);

  if (isLoading) {
    return (
      <div className="container max-w-6xl mx-auto py-12 px-4">
        <div className="text-center py-10">
          <p className="text-gray-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return notFound;
  }

  const purchaseOrTransaction =
    type === "purchase"
      ? (data as Purchase)
      : transactionToPurchase(data as Transaction);

  const formattedDate = data.ts
    ? format(new Date(data.ts), "MMMM d, yyyy 'at' h:mm a")
    : "N/A";

  const totalAmount = purchaseOrTransaction.transactions?.reduce(
    (acc, transaction) => (acc += transaction.amount),
    0,
  );

  const retiredAmount =
    purchaseOrTransaction.transactions?.reduce(
      (acc, transaction) => (acc += transaction.retiredAmount),
      0,
    ) || 0;

  const retirementPercentage =
    totalAmount > 0
      ? Math.min(Math.floor((retiredAmount / totalAmount) * 100), 100)
      : 0;

  const isFullyRetired = retirementPercentage === 100;
  const isPartiallyRetired =
    retirementPercentage > 0 && retirementPercentage < 100;
  const isNotRetired = retirementPercentage === 0;

  const primaryColor = purchaseOrTransaction.organization.primaryColor;

  const primaryColorLight = `${primaryColor}20`;
  const primaryColorMedium = `${primaryColor}40`;

  const headerBackgroundStyle = {
    backgroundImage: `url(https://cdn.tree.ly/assets/v5/fpm-app/background-shapes-transparent.svg)`,
    backgroundColor: primaryColor,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  const impactImageUrl =
    "/lovable-uploads/696c3fa3-6691-47a5-b1e0-dde90e26ac6d.png";

  const issuers = removeDuplicates(
    purchaseOrTransaction.transactions
      .flatMap((transaction) => [
        transaction.fromAccount?.defaultIssuer,
        transaction.toAccount?.defaultIssuer,
        transaction.project?.defaultIssuer,
      ])
      .filter(Boolean),
    (issuer) => issuer.id,
  );

  const batches = removeDuplicates(
    purchaseOrTransaction.transactions
      .map((transaction) => transaction.batch)
      .filter(Boolean),
    (batch) => batch.id,
  );

  const batchesWithPublicVintages = batches.filter((batch) => batch.title);

  const projects = removeDuplicates(
    purchaseOrTransaction.transactions
      .map((transaction) => transaction.project)
      .filter(Boolean),
    (project) => project.id,
  );

  const creditTypes = removeDuplicates(
    purchaseOrTransaction.transactions
      .map((transaction) => transaction.creditType)
      .filter(Boolean),
    (creditType) => creditType,
  );

  const hasProjectInfo = projects.length > 0;

  const formattedAmount = formatCO2eCredits(totalAmount, t, language);
  const shareTitle = t("confirmation.shareTitle").replace(
    "{amount}",
    formattedAmount,
  );

  const shareDescription = hasProjectInfo
    ? t("confirmation.shareDescriptionText", {
        projects: formatMultiple(
          projects.map(
            (project) =>
              getProjectTranslation(project.nameTranslations, language) ||
              project.title,
          ),
          t,
        ),
      })
    : t("confirmation.shareDescriptionGeneric");

  const customerName =
    purchaseOrTransaction.customerName &&
    !NAME_HIDDEN_CUSTOMER_NAMES.includes(purchaseOrTransaction.customerName)
      ? purchaseOrTransaction.customerName
      : "";

  const contributionMessage = (
    <p
      className="text-center text-sm md:text-base italic"
      style={{ color: primaryColor === "#4CAF50" ? "inherit" : primaryColor }}
    >
      <UnsafeMarkdown
        content={t(
          `confirmation.contribution.${hasProjectInfo ? "project" : "generic"}`,
          {
            customer: customerName,
            amount: formatCO2eCredits(totalAmount, t, language),
            projects: formatMultiple(
              projects.map(
                (project) =>
                  `<b>${getProjectTranslation(project.nameTranslations, language) || project.title}</b>`,
              ),
              t,
            ),
          },
        )}
      />
    </p>
  );

  const contributionBoxStyle = {
    backgroundColor: primaryColorLight,
    borderColor: primaryColorMedium,
  };

  return (
    <div className="min-h-screen py-12 bg-gray-50">
      <div className="container max-w-6xl mx-auto fade-in space-y-6 px-[24px]">
        <Card id={CONFIRMATION_CARD_ID} className="shadow-lg overflow-hidden">
          <div className="p-6 text-white" style={headerBackgroundStyle}>
            <div className="flex flex-col items-center justify-center space-y-3">
              <CheckCircle className="h-16 w-16" />
              <h2 className="text-center text-2xl md:text-3xl font-bold">
                {t(`confirmation.${type}.title`)}
              </h2>
              <p className="text-center text-base opacity-90">
                {t(`confirmation.${type}.subtitle`)}
              </p>
            </div>
          </div>

          <div
            className="p-6 border-b transition-all"
            style={contributionBoxStyle}
          >
            {contributionMessage}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            <div className="md:col-span-2 space-y-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2 text-gray-800">
                  {t(`confirmation.${type}.details`)}
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center">
                    <p className="text-sm text-gray-500 mr-1">
                      {t(`confirmation.${type}.id`)}:
                    </p>
                    <div className="flex items-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger
                            className="hover:underline cursor-help font-medium text-gray-900"
                            data-full-id={purchaseOrTransaction.id}
                          >
                            {cropId(purchaseOrTransaction.id)}
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs break-all">
                            <p>{purchaseOrTransaction.id}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 ml-1 text-gray-500 hover:text-gray-700"
                        onClick={() =>
                          copyToClipboard(
                            purchaseOrTransaction.id,
                            t(`confirmation.${type}.id`),
                            t,
                          )
                        }
                      >
                        <Copy className="h-3.5 w-3.5" />
                        <span className="sr-only">Copy transaction ID</span>
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("confirmation.dateTime")}
                    </p>
                    <p className="font-medium text-gray-900">{formattedDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("confirmation.amount")}
                    </p>
                    <p className="font-medium text-gray-900">
                      {formatCO2eCredits(totalAmount, t, language)}
                    </p>
                  </div>
                  {purchaseOrTransaction.description && (
                    <div>
                      <p className="text-sm text-gray-500">
                        {t(`confirmation.${type}.description`)}
                      </p>
                      <p className="font-medium text-gray-900">
                        {purchaseOrTransaction.description}
                      </p>
                    </div>
                  )}
                  {batchesWithPublicVintages.length > 0 &&
                    !BATCH_HIDDEN_CUSTOMER_NAMES.includes(
                      purchaseOrTransaction.customerName,
                    ) && (
                      <div>
                        <p className="text-sm text-gray-500">
                          {t("confirmation.batch")}
                        </p>
                        <p className="font-medium text-gray-900">
                          {batchesWithPublicVintages
                            .filter((batch) => batch.title)
                            .map((batch) => batch.title)
                            .sort()
                            .join(", ")}
                        </p>
                      </div>
                    )}
                  {hasProjectInfo && (
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("confirmation.project")}
                      </p>
                      {projects.map((project) => (
                        <div key={project.id}>
                          <Link
                            to={`/projects/${project.id}`}
                            className="font-medium text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <FileText className="h-4 w-4 mr-1.5" />
                            {getProjectTranslation(
                              project.nameTranslations,
                              language,
                            ) || project.title}
                            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                  {issuers.length > 0 && (
                    <div className="share-exclude">
                      <p className="text-sm text-gray-500">
                        {t("confirmation.issuer")}
                      </p>
                      <div className="flex items-center">
                        {issuers.map((issuer, index) => (
                          <div
                            key={issuer.id}
                            className={cn("flex items-center", {
                              "mr-4": index < issuers.length - 1,
                            })}
                          >
                            <Avatar className="h-8 w-8 mr-2">
                              <AvatarImage
                                src={issuer.logoUrl}
                                alt={issuer.name}
                              />
                              <AvatarFallback className="text-xs">
                                {issuer.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-gray-900">
                              {issuer.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {batches.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("confirmation.issuanceType.label")}
                      </p>
                      <p className="font-medium text-gray-900">
                        {Array.from(new Set(batches.map((b) => b.issuanceType)))
                          .map((type) => t(`confirmation.issuanceType.${type}`))
                          .join(", ")}
                      </p>
                    </div>
                  )}
                  {creditTypes && (
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("confirmation.creditType.label")}
                      </p>
                      <p className="font-medium text-gray-900">
                        {creditTypes
                          .map((type) => t(`confirmation.creditType.${type}`))
                          .join(", ")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-3 text-gray-800">
                  {t("confirmation.retirementStatus")}
                </h3>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1">
                    <Progress
                      value={retirementPercentage}
                      className={cn(
                        "h-2",
                        { "[&>div]:bg-green-500": isFullyRetired },
                        { "[&>div]:bg-purple-500": isPartiallyRetired },
                        { "[&>div]:bg-red-500": isNotRetired },
                      )}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {retirementPercentage}
                    {t("transaction.percentRetired")}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {isFullyRetired
                    ? t("confirmation.fullyRetired")
                    : isPartiallyRetired
                      ? t("confirmation.partiallyRetired")
                      : t("confirmation.notRetired")}
                </p>
                {!isNotRetired && (
                  <RetirementsList
                    transactionIds={purchaseOrTransaction.transactions.map(
                      (transaction) => transaction.id,
                    )}
                  />
                )}
              </div>

              <div
                id="account-information-container"
                className="p-4 bg-gray-50 rounded-lg"
              >
                <h3 className="font-semibold text-lg mb-3 text-gray-800">
                  {t("confirmation.accountInformation")}
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {purchaseOrTransaction.transactions.map((transaction) => (
                    <div>
                      <span className="p-1 font-semibold text-sm mb-3 text-gray-800">
                        Transaction {cropId(transaction.id)}
                      </span>

                      <Link
                        to={`/transactions?transactionId=${transaction.id}`}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 ml-1 text-gray-500 hover:text-gray-700"
                        >
                          <ArrowUpRightIcon className="h-3 w-3" />
                          <span className="sr-only">Copy account ID</span>
                        </Button>
                      </Link>
                      <div
                        key={transaction.id}
                        className="grid grid-cols-2 gap-4"
                      >
                        <TransactionAccountInformation
                          type={t("confirmation.from")}
                          account={transaction.fromAccount}
                        />
                        <TransactionAccountInformation
                          type={t("confirmation.to")}
                          account={transaction.toAccount}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col h-full">
              <div className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm h-full flex items-stretch">
                {impactImageUrl ? (
                  <img
                    src={impactImageUrl}
                    alt="Transaction impact visualization"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error("Image failed to load:", impactImageUrl);
                      const target = e.target as HTMLImageElement;
                      target.onerror = null; // Prevent infinite error loop
                      target.src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlNmU2ZTYiLz48cGF0aCBkPSJNMTAwIDEyNUMxMTQuMzggMTI1IDEyNiAxMTMuMzggMTI2IDk5QzEyNiA4NC42MiAxMTQuMzggNzMgMTAwIDczQzg1LjYyIDczIDc0IDg0LjYyIDc0IDk5Qzc0IDExMy4zOCA4NS42MiAxMjUgMTAwIDEyNVoiIGZpbGw9IiNjNGM0YzQiLz48cGF0aCBkPSJNNTUgMTYwQzU1IDE0NC4zMyA3Mi42NzYyIDEzNiA5OS41IDEzNkMxMjYuMzI0IDEzNiAxNDQgMTQ0LjMzIDE0NCAxNjAiIHN0cm9rZT0iI2M0YzRjNCIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9zdmc+";
                      target.classList.add("bg-gray-100");
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <Image className="h-16 w-16 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 space-y-4 sm:space-y-0">
            <div>
              <CardTitle className="text-xl">
                {t("confirmation.shareImpact")}
              </CardTitle>
              <CardDescription>
                {t("confirmation.shareDescription")}
              </CardDescription>
            </div>
            <div>
              <ShareButton
                title={shareTitle}
                description={shareDescription}
                elementId={CONFIRMATION_CARD_ID}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TransactionConfirmation;
